<script lang="ts">
import { computed, h, type Component } from 'vue';
import {
  Activity,
  AppWindow,
  Bell,
  Wrench,
  FolderOpen,
  AlignCenter,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignLeft,
  AlignRight,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  AlignVerticalJustifyCenter,
  ArrowDownToLine,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUpToLine,
  Blend,
  Bold,
  BringToFront,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Check,
  Copy,
  Crop,
  Diamond,
  Download,
  Ellipsis,
  EllipsisVertical,
  Eraser,
  Frame,
  Grid3x3,
  Group,
  Hand,
  House,
  Image,
  Info,
  Italic,
  Keyboard,
  LayoutGrid,
  LayoutList,
  Languages,
  Link,
  ListTree,
  Lock,
  LockOpen,
  Map,
  Maximize,
  Menu,
  Minus,
  Monitor,
  MousePointer2,
  MoveUpRight,
  Pencil,
  Pin,
  PenLine,
  PenTool,
  Waves,
  Plus,
  RefreshCw,
  Puzzle,
  Redo2,
  ScrollText,
  Search,
  SendToBack,
  Settings,
  Code,
  Gauge,
  PaintBucket,
  SlidersHorizontal,
  Slash,
  Spline,
  Square,
  StickyNote,
  Trash2,
  Triangle,
  TriangleAlert,
  Type,
  Undo2,
  Workflow,
  Ungroup,
  Upload,
  X,
  Palette,
  SquaresUnite,
  SquaresSubtract,
  SquaresIntersect,
  SquaresExclude,
  Combine,
  Slice,
  HardDrive,
  Sun,
  Moon,
  SunMoon,
  Underline,
  Strikethrough,
  Highlighter,
  Baseline,
  AArrowDown,
  AArrowUp,
  RemoveFormatting,
  PanelRight,
  PanelTop,
  Paintbrush,
  MessageCircle,
  MessageSquareText,
  Send,
  Tag,
  SmilePlus,
  Command,
  Proportions,
  Funnel,
  Bookmark,
  Link2,
  ExternalLink,
  Component as ComponentIcon,
  Lasso,
  RectangleHorizontal,
  Share2,
  User,
} from 'lucide-vue-next';

/** A Lucide-style icon of our own (flowchart forms Lucide lacks). */
function drawn(d: string, dash?: string): Component {
  return (p: { size?: number; strokeWidth?: number }) =>
    h('svg', { xmlns: 'http://www.w3.org/2000/svg', width: p.size ?? 24, height: p.size ?? 24, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': p.strokeWidth ?? 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, [h('path', { d, 'stroke-dasharray': dash })]);
}

/** Icons from the open Lucide set (ISC license), addressed by short names used across the app. */

const ICONS: Record<string, Component> = {
  select: MousePointer2,
  hand: Hand,
  rect: Square,
  pill: RectangleHorizontal,
  node: Share2,
  manualInput: drawn('M4 10.5 20 6v13H4z'),
  data: drawn('M8 6h14l-6 12H2z'),
  document: drawn('M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v13c-3-2-5 0-8 0s-5-3-8-1z'),
  display: drawn('M8 5h9c3 3 3 11 0 14H8l-5-7z'),
  bracket: drawn('M9 4H5v16h4M12 12h7'),
  brace: drawn('M4 4c2 0 3 1 3 3v3c0 1 1 2 2 2-1 0-2 1-2 2v3c0 2-1 3-3 3M13 12h7'),
  braceLeft: drawn('M20 4c-2 0-3 1-3 3v3c0 1-1 2-2 2 1 0 2 1 2 2v3c0 2 1 3 3 3M4 12h7'),
  ellipse: Circle,
  triangle: Triangle,
  diamond: Diamond,
  text: Type,
  sticker: StickyNote,
  frame: Frame,
  image: Image,
  line: Slash,
  arrow: MoveUpRight,
  connector: Spline,
  pen: Pencil,
  pin: Pin,
  path: PenTool,
  eraser: Eraser,
  undo: Undo2,
  redo: Redo2,
  trash: Trash2,
  copy: Copy,
  group: Group,
  ungroup: Ungroup,
  lock: Lock,
  unlock: LockOpen,
  front: BringToFront,
  back: SendToBack,
  plus: Plus,
  minus: Minus,
  fit: Maximize,
  menu: Menu,
  close: X,
  chevron: ChevronRight,
  chevronLeft: ChevronLeft,
  chevronDown: ChevronDown,
  settings: Settings,
  sliders: SlidersHorizontal,
  module: Puzzle,
  log: ScrollText,
  grid: Grid3x3,
  home: House,
  perf: Activity,
  download: Download,
  upload: Upload,
  dots: Ellipsis,
  dotsVertical: EllipsisVertical,
  alignLeft: AlignStartVertical,
  alignCenterX: AlignCenterVertical,
  alignRight: AlignEndVertical,
  alignTop: AlignStartHorizontal,
  alignCenterY: AlignCenterHorizontal,
  alignBottom: AlignEndHorizontal,
  distributeX: AlignHorizontalDistributeCenter,
  distributeY: AlignVerticalDistributeCenter,
  textLeft: AlignLeft,
  textCenter: AlignCenter,
  textRight: AlignRight,
  link: Link,
  warning: TriangleAlert,
  info: Info,
  tree: ListTree,
  search: Search,
  apps: LayoutGrid,
  tiles: LayoutGrid,
  list: LayoutList,
  dockSide: PanelRight,
  dockTop: PanelTop,
  map: Map,
  keyboard: Keyboard,
  opacity: Blend,
  crop: Crop,
  bold: Bold,
  italic: Italic,
  underline: Underline,
  strike: Strikethrough,
  highlight: Highlighter,
  textColor: Baseline,
  font: Type,
  smaller: AArrowDown,
  larger: AArrowUp,
  clearFormat: RemoveFormatting,
  vTop: ArrowUpToLine,
  vMiddle: AlignVerticalJustifyCenter,
  vBottom: ArrowDownToLine,
  language: Languages,
  monitor: Monitor,
  refresh: RefreshCw,
  code: Code,
  gauge: Gauge,
  fill: PaintBucket,
  diagram: Workflow,
  dirDown: ArrowDown,
  dirRight: ArrowRight,
  dirUp: ArrowUp,
  dirLeft: ArrowLeft,
  check: Check,
  palette: Palette,
  opUnion: SquaresUnite,
  opSubtract: SquaresSubtract,
  opIntersect: SquaresIntersect,
  opExclude: SquaresExclude,
  opDivide: Slice,
  shapeOps: Combine,
  storage: HardDrive,
  folder: FolderOpen,
  installApp: AppWindow,
  sun: Sun,
  moon: Moon,
  sunMoon: SunMoon,
  brush: Paintbrush,
  comment: MessageSquareText,
  chat: MessageCircle,
  bell: Bell,
  wrench: Wrench,
  user: User,
  send: Send,
  tag: Tag,
  reaction: SmilePlus,
  command: Command,
  ratio: Proportions,
  filter: Funnel,
  bookmark: Bookmark,
  hyperlink: Link2,
  external: ExternalLink,
  component: ComponentIcon,
  lasso: Lasso,
  brushInk: PenLine,
  brushHighlighter: Highlighter,
  strokeSharp: Activity,
  strokeSmooth: Waves,
};

/** Names of the platform icons (modules may refer to them for their top bar buttons). */
export const ICON_NAMES: ReadonlySet<string> = new Set(Object.keys(ICONS));
</script>

<script setup lang="ts">
const props = defineProps<{ name: string; size?: number }>();
const comp = computed(() => ICONS[props.name] ?? Puzzle);
</script>

<template>
  <component :is="comp" :size="size ?? 18" :stroke-width="1.75" aria-hidden="true" />
</template>
